
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { User, UserRole } from '../types';
import Button from '../components/Button';
import { api } from '../services/api';

interface ClassGroup {
    id: string;
    name: string;
    grade: number;
    subject: string;
    class_code: string;
    student_count: number;
    avg_score: number;
}

const TeacherDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState<User | null>(null);
    const [classes, setClasses] = useState<ClassGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateClass, setShowCreateClass] = useState(false);

    // Create Class Form
    const [newClass, setNewClass] = useState({
        name: '',
        grade: 3,
        subject: 'Math'
    });

    useEffect(() => {
        const init = async () => {
            const currentUser = authService.getCurrentUser() || await authService.refreshUser();

            if (!currentUser || currentUser.role !== UserRole.TEACHER) {
                navigate('/login');
                return;
            }

            setUser(currentUser);
            await fetchClasses();
            setLoading(false);
        };

        init();
    }, [navigate]);

    const fetchClasses = async () => {
        try {
            const data = await api.get('/teacher/classes');
            setClasses(data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleCreateClass = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await api.post('/teacher/classes', newClass);
            setShowCreateClass(false);
            setNewClass({ name: '', grade: 3, subject: 'Math' }); // Reset form
            fetchClasses();
        } catch (err) {
            alert("Failed to create class");
        }
    };

    if (loading || !user) return <div className="p-8 text-center text-slate-500">Loading classroom tools...</div>;

    return (
        <div className="max-w-6xl mx-auto px-4 py-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white p-8 rounded-3xl shadow-xl mb-8">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold mb-2">🍎 Teacher Dashboard</h1>
                        <p className="text-teal-100">Welcome back, {user.name}!</p>
                    </div>
                    <Button
                        variant="secondary"
                        className="bg-white text-teal-700 hover:bg-teal-50"
                        onClick={() => setShowCreateClass(true)}
                    >
                        <i className="fas fa-plus mr-2"></i> Create Class
                    </Button>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                    <div className="text-slate-500 text-sm font-bold uppercase mb-1">Total Students</div>
                    <div className="text-3xl font-bold text-slate-800">0</div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                    <div className="text-slate-500 text-sm font-bold uppercase mb-1">Active Classes</div>
                    <div className="text-3xl font-bold text-brand-blue">{classes.length}</div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                    <div className="text-slate-500 text-sm font-bold uppercase mb-1">Assignments</div>
                    <div className="text-3xl font-bold text-teal-600">0</div>
                </div>
            </div>

            {/* Classes List */}
            <h2 className="text-xl font-bold text-slate-800 mb-4">My Classes</h2>

            {classes.length === 0 ? (
                <div className="bg-white p-12 rounded-3xl text-center border border-slate-100 shadow-sm">
                    <div className="text-5xl mb-4">🏫</div>
                    <h3 className="text-xl font-bold text-slate-700 mb-2">No Classes Yet</h3>
                    <p className="text-slate-500 mb-6 max-w-md mx-auto">Create your first class group to start adding students and assigning quizzes.</p>
                    <Button onClick={() => setShowCreateClass(true)}>Create Your First Class</Button>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {classes.map(cls => (
                        <div key={cls.id} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/teacher/classes/${cls.id}`)}>
                            <div className="flex justify-between items-start mb-4">
                                <div className="w-12 h-12 rounded-xl bg-teal-100 text-teal-600 flex items-center justify-center text-2xl">
                                    {cls.subject === 'Math' && '📐'}
                                    {cls.subject === 'Science' && '🧬'}
                                    {cls.subject === 'Language Arts' && '📚'}
                                    {cls.subject === 'Social Studies' && '🌍'}
                                    {cls.subject === 'General' && '🎓'}
                                </div>
                                <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-1 rounded-lg">
                                    {cls.class_code}
                                </span>
                            </div>
                            <h3 className="text-lg font-bold text-slate-800 mb-1">{cls.name}</h3>
                            <p className="text-slate-500 text-sm mb-4">{cls.grade === 0 ? 'Kindergarten' : `Grade ${cls.grade}`} • {cls.subject}</p>

                            <div className="flex items-center justify-between text-sm border-t border-slate-50 pt-3">
                                <span className="text-slate-500"><i className="fas fa-users mr-1"></i> {cls.student_count} Students</span>
                                <span className="text-teal-600 font-bold">View <i className="fas fa-arrow-right ml-1"></i></span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Class Modal */}
            {showCreateClass && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">
                        <h2 className="text-2xl font-bold text-slate-800 mb-6">Create New Class</h2>
                        <form onSubmit={handleCreateClass} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Class Name</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 outline-none focus:border-teal-500"
                                    placeholder="e.g. Mrs. Smith's 3rd Grade"
                                    value={newClass.name}
                                    onChange={e => setNewClass({ ...newClass, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Grade</label>
                                    <select
                                        className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 outline-none focus:border-teal-500 bg-white"
                                        value={newClass.grade}
                                        onChange={e => setNewClass({ ...newClass, grade: Number(e.target.value) })}
                                    >
                                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(g => <option key={g} value={g}>{g === 0 ? 'K' : g}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Subject</label>
                                    <select
                                        className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 outline-none focus:border-teal-500 bg-white"
                                        value={newClass.subject}
                                        onChange={e => setNewClass({ ...newClass, subject: e.target.value })}
                                    >
                                        <option>General</option>
                                        <option>Math</option>
                                        <option>Science</option>
                                        <option>Language Arts</option>
                                        <option>Social Studies</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex gap-3 mt-6">
                                <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700">Create</Button>
                                <Button variant="outline" type="button" onClick={() => setShowCreateClass(false)} className="flex-1">Cancel</Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
};

export default TeacherDashboard;
